import React, { useCallback } from "react";
import { Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../../../components/Dialog/DialogHeaderWithClose.component";
import { SeeditDialogContent } from "../../../components/Dialog/SeeditDialogContent.component";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus
} from "../types/dashboard2-work-status.types";
import { extraBookingCount } from "../utils/dashboard2-status-display.utils";
import { Dashboard2AdditionalBookingsList } from "./Dashboard2AdditionalBookingsList";

type Dashboard2DayBookingsDialogProps = {
  open: boolean;
  dayLabel: string;
  status: DayWorkStatus;
  onClose: () => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
};

export const Dashboard2DayBookingsDialog: React.FC<Dashboard2DayBookingsDialogProps> = ({
  open,
  dayLabel,
  status,
  onClose,
  onBookingClick
}) => {
  const { t } = useTranslation();
  const bookings = status.additionalBookings ?? [];

  const handleBookingClick = useCallback(
    (booking: Dashboard2DayBookingSummary) => {
      if (!onBookingClick) {
        return;
      }

      onBookingClick(booking);
      onClose();
    },
    [onBookingClick, onClose]
  );

  if (extraBookingCount(status) === 0) {
    return null;
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xs" data-testid="dashboard2-day-bookings-dialog">
      <DialogHeaderWithClose
        title={t("_dashboard2_dayBookingsTitle", { day: dayLabel })}
        onClose={onClose}
        closeTestId="dashboard2-day-bookings-close"
      />
      <SeeditDialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("_dashboard2_additionalBookingsTitle")}
        </Typography>
        <Dashboard2AdditionalBookingsList bookings={bookings} onBookingClick={handleBookingClick} />
      </SeeditDialogContent>
    </Dialog>
  );
};
